import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import { Button, Box } from '@mui/material';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { UserContext } from '../../../contexts/User';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import '../settings.css';
import CustomTable from '../../../components/table/CustomTable';
import useDebounce from '../../../hooks/useDebounce';
import DropDown from '../../../components/dropdown';
import EditDepartmentModal from './EditModal';
import DepartmentDeleteModal from './DeleteModal';

const TABLE_HEADERS = [
  {
    label: 'Department Name',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'name',
    colSpan: 1.5,
  },
  {
    label: 'Date Created',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'createdAt',
  },
  {
    label: 'Action',
    sortable: false,
  },
];

const CustomTableStyles = {
  container: {
    marginTop: 1,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '16px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      textWrap: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
    },
  },
};

function DepartmentsTab() {
  const { activeWorkspace } = useContext(UserContext);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState({});
  const { debounce } = useDebounce();

  const getDepartments = useCallback(async (params) => {
    if (activeWorkspace) {
      setIsLoading(true);
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.departments(activeWorkspace.id),
        params,
      });

      if (response.success) {
        if (response.data?.departments?.length) {
          const mappedDepartments = response.data?.departments.map((department) => ({
            name: department.name,
            createdAt: department.createdAt,
            actions: <DropDown
              title="Actions"
              options={[
                {
                  text: 'Edit Department',
                  fontColor: '#061232',
                  handleClick: () => {
                    setSelectedDepartment(department);
                    setIsEditing(true);
                    setShowEditModal(true);
                  },
                },
                {
                  text: 'Delete Department',
                  fontColor: '#CC4525',
                  handleClick: () => {
                    setSelectedDepartment(department);
                    setShowDeleteConfirmModal(true);
                  },
                },
              ]}
            />,
          }));
          setDepartments(mappedDepartments);
        }
        setIsLoading(false);
      }
    }
  }, [activeWorkspace]);

  const handleSort = debounce((sortKey, sortOrder) => getDepartments({
    name: searchQuery,
    sortKey,
    sortOrder,
  }), 250);

  const fetchResults = async () => {
    const params = {
      name: searchQuery,
    };
    await getDepartments(params);
  };

  const handleSearch = useCallback(debounce((query) => setSearchQuery(query), 500), []);

  useEffect(() => {
    fetchResults();
  }, [searchQuery]);

  return (
    <>
      {
        showEditModal && (
        <EditDepartmentModal
          department={selectedDepartment}
          isEditing={isEditing}
          closeModal={() => setShowEditModal(false)}
          onDepartmentSaved={() => fetchResults()}
          workspaceId={activeWorkspace.id}
        />
        )
      }
      {
        showDeleteConfirmModal && (
        <DepartmentDeleteModal
          department={selectedDepartment}
          closeModal={() => setShowDeleteConfirmModal(false)}
          getData={() => fetchResults()}
        />
        )
      }
      <Box className="px-3 py-2 d-flex justify-content-between align-items-center">
        <Box>
          <Box className="size-s text-600 lh-base fc-dark">
            Departments
          </Box>
          <Box className="size-xs text-400 lh-sm fc-dark">
            A department is a functional unit within your organization
          </Box>
        </Box>
        <Box className="d-flex align-items-center">
          <div className="searchBox mr-2">
            <input
              type="text"
              placeholder="Search Departments"
              onChange={(e) => handleSearch(e.target.value)}
            />
            <i className="fa fa-search searchIcon" />
          </div>
          <Button
            variant="contained"
            onClick={() => {
              setIsEditing(false);
              setShowEditModal(true);
              setSelectedDepartment({});
            }}
            data-testid="new-department"
          >
            Add Department
          </Button>
        </Box>
      </Box>
      <CustomTable
        headers={TABLE_HEADERS}
        items={departments}
        loading={isLoading}
        handleSort={handleSort}
        styles={CustomTableStyles}
      />
    </>
  );
}

export default DepartmentsTab;
